export const AUTH_SESSION_EXPIRED_EVENT = 'ergshoot:auth-session-expired';

const AUTHENTICATED_SESSION_KEY = 'ergshoot_authenticated_session';

type StatusLike = Pick<Response, 'status'>;

function resolveSessionStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function sanitizeInternalPath(path: string | null | undefined): string | null {
  if (!path?.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;
  return path;
}

export function buildLoginPath(destination: string, expired: boolean): string {
  const safeDestination = sanitizeInternalPath(destination) || '/dashboard';
  const params = new URLSearchParams();
  if (expired) params.set('reason', 'session_expired');
  params.set('redirect', safeDestination);
  return `/login?${params.toString()}`;
}

export function getPostLoginPath(search: string): string {
  const destination = new URLSearchParams(search).get('redirect');
  return sanitizeInternalPath(destination) || '/dashboard';
}

export function isUnauthorizedResponse(response: StatusLike): boolean {
  return response.status === 401;
}

export function markAuthenticatedSession(generation: string, storage?: Storage): void {
  const targetStorage = resolveSessionStorage(storage);
  if (!targetStorage) return;

  try {
    targetStorage.setItem(AUTHENTICATED_SESSION_KEY, generation);
  } catch {
    // Session recovery is best-effort when browser storage is blocked.
  }
}

export function clearAuthenticatedSession(storage?: Storage): void {
  const targetStorage = resolveSessionStorage(storage);
  if (!targetStorage) return;

  try {
    targetStorage.removeItem(AUTHENTICATED_SESSION_KEY);
  } catch {
    // Explicit Clerk sign-out must remain usable without browser storage.
  }
}

export function hasAuthenticatedSession(storage?: Storage): boolean {
  return getAuthenticatedSessionGeneration(storage) !== null;
}

export function getAuthenticatedSessionGeneration(storage?: Storage): string | null {
  const targetStorage = resolveSessionStorage(storage);
  if (!targetStorage) return null;

  try {
    return targetStorage.getItem(AUTHENTICATED_SESSION_KEY);
  } catch {
    return null;
  }
}

export function clearSessionMarkerForSignedOutLogin(
  isLoaded: boolean,
  isSignedIn: boolean | undefined,
  storage?: Storage
): void {
  if (isLoaded && !isSignedIn) clearAuthenticatedSession(storage);
}

export async function signOutExplicitly(
  signOut: () => Promise<unknown>,
  storage?: Storage
): Promise<void> {
  const generation = getAuthenticatedSessionGeneration(storage);
  clearAuthenticatedSession(storage);
  try {
    await signOut();
  } catch (error) {
    if (generation) markAuthenticatedSession(generation, storage);
    throw error;
  }
}

export function notifySessionExpired(target: EventTarget = window): void {
  target.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
}

export function getCurrentDestination(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
): string {
  return `${location.pathname}${location.search}${location.hash}`;
}
